import React, { useState, useEffect } from 'react';
import { formatPhoneInput } from './booking/utils';
import FormRenderer, { matchFormType } from './forms/FormRenderer';
import './BookingFlow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function FormsPage() {
  const [step, setStep] = useState('phone'); // phone, otp, forms, filling
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [guestInfo, setGuestInfo] = useState(null);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeForm, setActiveForm] = useState(null);

  // Auto-submit OTP when 6 digits entered
  useEffect(() => {
    if (otp.length === 6 && step === 'otp') {
      handleVerifyOtp();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  async function handleSendOtp() {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: phone })
      });
      const data = await res.json();
      if (data.success) {
        setVerificationId(data.verificationId);
        setStep('otp');
      } else {
        setError(data.error || 'Failed to send code.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  }

  async function handleVerifyOtp() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName: phone, otp, verificationId })
      });
      const data = await res.json();
      if (data.success && data.guestId) {
        setGuestInfo(data);
        setStep('forms');
        loadForms(data.guestId);
      } else {
        setError(data.error || 'Invalid code. Please try again.');
        setOtp('');
      }
    } catch {
      setError('Verification failed. Please try again.');
      setOtp('');
    }
    setLoading(false);
  }

  async function loadForms(guestId) {
    setFormsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/account/forms/${guestId}`);
      const data = await res.json();
      setForms(data.forms || []);
    } catch {
      setForms([]);
    }
    setFormsLoading(false);
  }

  function handleOpenForm(form) {
    const formType = matchFormType(form.name);
    setActiveForm({ ...form, customFormType: formType });
    setStep('filling');
    // Scroll to top when opening form
    window.scrollTo(0, 0);
  }

  function handleFormComplete() {
    setActiveForm(null);
    setStep('forms');
    // Reload forms to get updated filled status
    if (guestInfo?.guestId) loadForms(guestInfo.guestId);
    window.scrollTo(0, 0);
  }

  function handleFormBack() {
    setActiveForm(null);
    setStep('forms');
    if (guestInfo?.guestId) loadForms(guestInfo.guestId);
    window.scrollTo(0, 0);
  }

  const unfilledForms = forms.filter(f => !f.filled);
  const filledForms = forms.filter(f => f.filled);

  return (
    <div className="forms-page">
      <div className="forms-page-inner">

        {/* Phone & OTP steps keep the logo */}
        {(step === 'phone' || step === 'otp') && (
          <div className="forms-page-logo">
            <img src="/logo512.png" alt="Cutie Pie Wax Bar" className="forms-page-logo-img" />
          </div>
        )}

        {step === 'phone' && (
          <div className="forms-page-card">
            <h1 className="forms-page-title">Complete Your Forms</h1>
            <p className="forms-page-sub">Enter your phone number to access your forms.</p>
            <div className="auth-field">
              <label className="auth-label">PHONE NUMBER</label>
              <input
                type="tel"
                className="auth-input"
                placeholder="(604) 555-1234"
                value={phone}
                onChange={(e) => { setPhone(formatPhoneInput(e.target.value)); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSendOtp()}
                autoFocus
              />
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button className="auth-button" onClick={handleSendOtp} disabled={loading}>
              {loading ? 'SENDING...' : 'SEND VERIFICATION CODE'}
            </button>
          </div>
        )}

        {step === 'otp' && (
          <div className="forms-page-card">
            <h1 className="forms-page-title">Enter Verification Code</h1>
            <p className="forms-page-sub">We sent a 6-digit code to <strong>{phone}</strong></p>
            <div className="auth-field">
              <label className="auth-label">VERIFICATION CODE</label>
              <input
                type="text"
                className="auth-input auth-input-otp"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyOtp()}
                autoFocus
                maxLength={6}
              />
            </div>
            {error && <p className="auth-error">{error}</p>}
            <button className="auth-button" onClick={handleVerifyOtp} disabled={loading || otp.length < 6}>
              {loading ? 'VERIFYING...' : 'VERIFY'}
            </button>
            <button className="auth-link" onClick={() => { setError(''); handleSendOtp(); }} disabled={loading}>
              Resend code
            </button>
            <button className="auth-link" onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>
              Use a different number
            </button>
          </div>
        )}

        {step === 'filling' && activeForm && (
          activeForm.customFormType ? (
            <FormRenderer
              formType={activeForm.customFormType}
              guestInfo={guestInfo}
              formMeta={activeForm}
              onComplete={handleFormComplete}
              onBack={handleFormBack}
            />
          ) : (
            // Fallback: open in new tab for unrecognized form types
            <div className="forms-page-card">
              <h1 className="forms-page-title">{activeForm.name}</h1>
              <p className="forms-page-sub">This form will open in a new window.</p>
              <a
                href={activeForm.formUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="auth-button"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 12 }}
              >
                OPEN FORM
              </a>
              <button className="auth-link" onClick={handleFormBack}>
                Back to forms
              </button>
            </div>
          )
        )}

        {step === 'forms' && (
          <>
            <div className="forms-page-logo">
              <img src="/logo512.png" alt="Cutie Pie Wax Bar" className="forms-page-logo-img" />
            </div>
            <div className="forms-page-card">
              <h1 className="forms-page-title">
                {guestInfo?.firstName ? `Hi ${guestInfo.firstName}!` : 'Your Forms'}
              </h1>
              {formsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                  Loading your forms...
                </div>
              ) : unfilledForms.length > 0 ? (
                <>
                  <p className="forms-page-sub">Please complete these forms before your appointment.</p>
                  <div className="forms-page-list">
                    {unfilledForms.map((form, i) => (
                      <div
                        key={i}
                        className="forms-page-form-card"
                        onClick={() => handleOpenForm(form)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="forms-page-form-info">
                          <div className="forms-page-form-name">{form.name}</div>
                          {form.serviceName && <div className="forms-page-form-service">{form.serviceName}</div>}
                          <div className="forms-page-form-type">{form.formType === 'service' ? 'Service Form' : 'Guest Form'}</div>
                        </div>
                        <div className="forms-page-form-arrow">
                          {matchFormType(form.name) ? 'Fill out \u2192' : 'Open \u2192'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {filledForms.length > 0 && (
                    <div className="forms-page-completed">
                      <div className="forms-page-completed-title">Completed</div>
                      {filledForms.map((form, i) => (
                        <div key={i} className="forms-page-form-card completed">
                          <div className="forms-page-form-info">
                            <div className="forms-page-form-name">{form.name}</div>
                            {form.serviceName && <div className="forms-page-form-service">{form.serviceName}</div>}
                          </div>
                          <div className="forms-page-form-check">{'\u2713'}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : forms.length > 0 ? (
                <div className="forms-page-all-done">
                  <div className="forms-page-all-done-icon">{'\u2713'}</div>
                  <p className="forms-page-sub">All forms are complete! You're all set.</p>
                </div>
              ) : (
                <p className="forms-page-sub">No forms to fill out right now.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FormsPage;
